import {
  aeEmirateGroupOf,
  type AeEmirate,
  type AeEmirateGroup,
} from './emirates';

/**
 * UAE delivery-promise windows (Asia/Dubai, UTC+4, no DST). Pure module, no
 * React — mirrors getIstParts in ../DeliveryEtaBadge/useSddNddEligibility.ts
 * but is purely frontend: no pincode, no serviceability API.
 *
 * Windows per emirate group (ops sheet, Oct 2026, revised 2026-10-13 —
 * matches the checkout shipping rates: no 4-hour service outside Dubai):
 *
 *   dubai              00:00–09:59 same_day · 10:00–16:59 four_hour · 17:00– next_day
 *   abu_dhabi_sharjah  00:00–12:59 same_day · 13:00– next_day (never four_hour)
 *   ineligible         next_day at every hour — eligibility is a property of
 *                      the emirate, not the time of day, so the resolver
 *                      never reads the clock for this group.
 */
export type AeDeliveryWindow = 'same_day' | 'four_hour' | 'next_day';

/** Window plus the TSV error row: timezone lookup threw (exotic browsers
 * without Asia/Dubai Intl data). Produced by useAeDeliveryWindow and
 * resolveAeDeliveryWindowState, never by the bare resolver. */
export type AeDeliveryWindowState = AeDeliveryWindow | 'error';

/** Ordered minutes-of-day tiers, Asia/Dubai. The first tier whose `endMinutes`
 * is still ahead of the clock wins (ends exclusive at the minute); past every
 * tier the group is next_day. An empty list means next_day at every hour
 * without ever consulting the clock. */
export const AE_WINDOW_BOUNDS: Record<
  AeEmirateGroup,
  ReadonlyArray<{ endMinutes: number; window: AeDeliveryWindow }>
> = {
  dubai: [
    { endMinutes: 10 * 60, window: 'same_day' },
    { endMinutes: 17 * 60, window: 'four_hour' },
  ],
  abu_dhabi_sharjah: [{ endMinutes: 13 * 60, window: 'same_day' }],
  ineligible: [],
};

type DubaiParts = { hour: number; minute: number; second: number };

// Module-level singleton — constructing Intl.DateTimeFormat is comparatively
// expensive and this runs on every render tick / timer sync.
const dubaiTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Dubai',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const getDubaiParts = (date: Date): DubaiParts => {
  const parts = dubaiTimeFormatter.formatToParts(date);
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? '0';
  return {
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
  };
};

export const resolveAeDeliveryWindow = (
  group: AeEmirateGroup,
  now: Date = new Date(),
): AeDeliveryWindow => {
  const bounds = AE_WINDOW_BOUNDS[group];
  if (bounds.length === 0) return 'next_day';
  const { hour, minute } = getDubaiParts(now);
  const minutesOfDay = hour * 60 + minute;
  return (
    bounds.find((tier) => minutesOfDay < tier.endMinutes)?.window ?? 'next_day'
  );
};

/** Union of every group's boundaries plus midnight, in minutes-of-day —
 * DERIVED from AE_WINDOW_BOUNDS so an ops cutoff change can never leave the
 * timer schedule behind. Group-agnostic so a timer armed on it never needs
 * re-arming when the user switches emirate; a tick at a boundary the current
 * group doesn't use re-resolves to the same window and React bails out on
 * the identical string state. */
const AE_BOUNDARY_MINUTES = [
  ...new Set(
    Object.values(AE_WINDOW_BOUNDS).flatMap((bounds) =>
      bounds.map((tier) => tier.endMinutes),
    ),
  ),
  24 * 60,
].sort((a, b) => a - b);

/** Milliseconds until the next boundary, so a mounted component can
 * re-render exactly when the window flips instead of polling. +50ms guards
 * against firing a tick early from timer jitter. */
export const msUntilNextAeBoundary = (now: Date = new Date()): number => {
  const { hour, minute, second } = getDubaiParts(now);
  const secondsOfDay = hour * 3600 + minute * 60 + second;
  const nextBoundarySeconds =
    AE_BOUNDARY_MINUTES.map((m) => m * 60).find(
      (boundary) => boundary > secondsOfDay,
    ) ?? 24 * 3600;
  const msUntil =
    (nextBoundarySeconds - secondsOfDay) * 1000 - now.getMilliseconds() + 50;
  return Math.max(msUntil, 0);
};

/** Non-throwing emirate-level resolver for analytics/checkout call sites
 * (null emirate = the documented best-case dubai default). A resolver throw
 * (e.g. an invalid Date) becomes 'error' instead of crashing the caller
 * such as checkout. Note: a runtime with no Asia/Dubai Intl data throws at
 * module load (the formatter above is module-scope), which no function-level
 * catch can reach — same pre-existing behavior as useAeDeliveryWindow. */
export const resolveAeDeliveryWindowState = (
  emirate: AeEmirate | null,
  now: Date = new Date(),
): AeDeliveryWindowState => {
  try {
    return resolveAeDeliveryWindow(aeEmirateGroupOf(emirate), now);
  } catch {
    return 'error';
  }
};

/** True only on the AE ring buy page while the kill switch is on.
 * Better-help has no emirate selector and always shows the flat next-day
 * line, so a tab's stored emirate must not be restored or stamped there. */
export const recordsAeEmirateSelection = (
  isAeRegion: boolean,
  enabled: boolean,
  isBetterHelp: boolean,
): boolean => isAeRegion && enabled && !isBetterHelp;
